import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Download, Home } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  trackPageView,
  trackPDFDownload,
  trackCTAClick,
} from "../utils/analytics";
import { trackCAPICompleteRegistration } from "../utils/metaConversionsAPI";

export const ThankYouPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "application";
  const source = searchParams.get("source") || "unknown";

  const isDownloadMode = mode === "download";

  // Track page view on mount
  useEffect(() => {
    trackPageView(
      window.location.pathname + window.location.search,
      "Thank You - The Free Birds Collective"
    );

    // Track conversion page view
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "conversion_page_view", {
        event_category: "Conversion",
        event_label: isDownloadMode
          ? "PDF Download Page"
          : "Application Thank You Page",
        form_mode: mode,
        form_source: source,
        value: isDownloadMode ? 10 : 100,
      });
    }

    // Generate unique event ID for deduplication
    const registrationEventId = `registration_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Track Meta Pixel CompleteRegistration event (browser-side)
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      if (isDownloadMode) {
        window.fbq("track", "CompleteRegistration", {
          content_name: "Course Details Download",
          status: "completed",
        }, { eventID: registrationEventId });
      } else {
        window.fbq("track", "CompleteRegistration", {
          content_name: "Acting Programme Application",
          status: "completed",
        }, { eventID: registrationEventId });
      }
    }

    // Track Meta Conversions API CompleteRegistration event (server-side) with same event ID
    trackCAPICompleteRegistration(mode, {}, registrationEventId);
  }, [mode, source, isDownloadMode]);

  const handleDownload = () => {
    trackPDFDownload();
    trackCTAClick("thank_you_page", "Download PDF", "thank_you_page");

    const link = document.createElement("a");
    link.href = "/TFBC course details.pdf";
    link.download = "TFBC-course-details.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGoHome = () => {
    trackCTAClick("thank_you_page", "Go Home", "thank_you_page");
    navigate("/");
  };

  // Auto-download for download mode
  useEffect(() => {
    if (isDownloadMode) {
      // Small delay to ensure page loads
      const timer = setTimeout(() => {
        trackPDFDownload();
        const link = document.createElement("a");
        link.href = "/TFBC course details.pdf";
        link.download = "TFBC-course-details.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isDownloadMode]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-purple-50 flex items-center justify-center px-6 py-12">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>

          {/* Heading */}
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {isDownloadMode ? "Download Started!" : "Application Submitted!"}
          </h1>

          {/* Message */}
          <div className="space-y-4 mb-8">
            <p className="text-xl lg:text-2xl text-gray-600">
              {isDownloadMode ? (
                "Your download should begin automatically."
              ) : (
                <>
                  Thank you for applying to
                  <br />
                  <span className="font-bold text-purple-600">
                    The Free Birds Collective
                  </span>
                </>
              )}
            </p>
            <p className="text-gray-500 text-lg">
              {isDownloadMode ? (
                "If it doesn't, use the button below to download manually."
              ) : (
                <>
                  Our admissions team will contact you
                  <br />
                  <span className="font-semibold text-gray-700">
                    within 24 hours
                  </span>
                </>
              )}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {isDownloadMode ? (
              <>
                <Button
                  onClick={handleDownload}
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-lg font-semibold px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download PDF Again
                </Button>
                <Button
                  onClick={handleGoHome}
                  variant="outline"
                  size="lg"
                  className="border-2 border-purple-600 text-purple-600 hover:bg-purple-50 text-lg font-semibold px-8 py-6"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Go to Homepage
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleDownload}
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-lg font-semibold px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Course Details
                </Button>
                <Button
                  onClick={handleGoHome}
                  variant="outline"
                  size="lg"
                  className="border-2 border-purple-600 text-purple-600 hover:bg-purple-50 text-lg font-semibold px-8 py-6"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Return to Homepage
                </Button>
              </>
            )}
          </div>

          {/* Additional Info */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              🔒 Your information is secure. We'll contact you via WhatsApp/Call
              within 24 hours.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
